
import { Component, Vue, Watch } from "vue-property-decorator";
import { namespace, Getter } from "vuex-class";
import { get } from "lodash";
import moment from "moment";
import vSelect from "vue-select";
import api from "@/services/api.service";
import NavHeader from "@/components/NavHeader.vue";
import { Tenant } from "@/store/areas/types";
import { UserPermissions } from "@/store/types";

interface Customer {
    id: number;
    name: string;
    email: string;
}

interface LicenseDetailsDto {
    licenseKey: string;
    customer: Customer
    expirationDate: Date;
    maxGuards: number;
    maxOperators: number;
    maxCameras: number;
    isValid: boolean;
    licenseType: string;
}

const License = namespace("license");
const Areas = namespace("areas");

@Component({
    components: {
        "nav-header": NavHeader,
        "v-select": vSelect
    }
})
export default class LicenseDetails extends Vue {
    @Getter getPermissions: UserPermissions;

    @License.Action fetchLicenseDetails: any;
    @License.Action setLicenseWithFile: any;
    @License.State licenseDetails: LicenseDetailsDto;

    @Areas.Action getTenants: any;

    private isSettingLicense: boolean = false;
    private licenseFile = null;
    private isLicenseLoaded = false;
    private isUploadingLicense = false;
    private defaultLicenseKey = "00000000-0000-0000-0000-000000000000";

    private licenseFeaturesTableFields = ['feature', 'description', 'currentUse', 'limit'];
    private licenseFeaturesTableData = [
        { feature: 'Max Operators', description: "The maximum number of Operators", currentUse: 0, limit: 0 },
        { feature: 'Max Guards', description: "The maximum number of Guards", currentUse: 0, limit: 0 },
        { feature: 'Max Cameras', description: "The maximum number of Cameras", currentUse: 0, limit: 0 }
    ];

    private selectedTenant: Tenant = null;
    private tenantsList: Tenant[] = [];

    public async mounted() {
        await this.loadAllTenants();
    }

    private async loadAllTenants() {
        this.tenantsList = await this.getTenants();

        // set the selected Tenant based on the isUsersTenant value
        this.tenantsList.forEach(tenant => {
            if (tenant.isUsersTenant) {
                this.selectedTenant = tenant;
            }
        });
    }
    
    private async loadLicenseForTenant() {
        this.isLicenseLoaded = false;
        await this.fetchLicenseDetails(this.selectedTenantGroupId);
        await this.updateLicenseDetailsTable();
        this.isLicenseLoaded = true;
    }

    private async updateLicenseDetailsTable() {
        const upperLimitMax = 2000000000;
        this.licenseFeaturesTableData[0].currentUse = await api.getTotalOperatorUsage(this.selectedTenantGroupId);
        this.licenseFeaturesTableData[0].limit = this.licenseDetails.maxOperators > upperLimitMax ? Infinity : this.licenseDetails.maxOperators;
        this.licenseFeaturesTableData[1].currentUse = await api.getTotalGuardUsage(this.selectedTenantGroupId);
        this.licenseFeaturesTableData[1].limit =  this.licenseDetails.maxGuards > upperLimitMax ? Infinity : this.licenseDetails.maxGuards;
        this.licenseFeaturesTableData[2].currentUse = await api.getTotalCameraUsage(this.selectedTenantGroupId);
        this.licenseFeaturesTableData[2].limit = this.licenseDetails.maxCameras > upperLimitMax ? Infinity : this.licenseDetails.maxCameras;
    }

    private get selectedTenantGroupId() {
        return get(this.selectedTenant, "groupID");
    }

    @Watch("selectedTenant")
    private async onSelectedTenantChange(old, newval)
    {
        await this.loadLicenseForTenant();
    }

    private async submitLicense() {
        this.isUploadingLicense = true;
        this.setLicenseWithFile({ licenseFile: this.licenseFile, tenantGroupId: this.selectedTenantGroupId }).then(() => {
            this.$notify({
                type: "success",
                title: "Successfully Updated License"
            });
        })
        .catch((error) => {
            // If the file being uploaded contains invalid characters it will be intercepted
            // and refused by the API throwing a generic 500 exception with no response
            // for this and any other unhandled exceptions, this will be the default message.
            var errorMessage = "Check the content of the License File";

            // If there's a custom exception message - use it instead.
            if (error && error.response && error.response.data) {
                errorMessage = error.response.data
            }

            this.$notify({
                type: "error",
                title: "Failed to upload License",
                text: errorMessage
            });
        })
        .finally(() => {
            this.isUploadingLicense = false;
            this.updateLicenseDetailsTable();
        });
    }

    private get getLicenseDetailsCardTitle() {
        return this.licenseDetails.isValid ? "License is Valid" : "License is invalid";
    }

    private get getLicenseExpiryAsUTC() {
        return moment.utc(this.licenseDetails.expirationDate, moment.defaultFormatUtc).format('LL LT');
    }

    private get getLicenseExpiryAsLocalTime() {
        return moment(this.licenseDetails.expirationDate).format('LL LT');
    }

    private get getIsLicenseExpired() {
        return moment(this.licenseDetails.expirationDate).isBefore(moment());
    }
}
